/**
 * Arctic Plate — seller compliance & marketplace safety constants.
 *
 * Single source of truth for:
 *   • Homemade-food disclosure text (shown on seller apply, item detail, checkout)
 *   • Prohibited home-kitchen categories/keywords (blocked from public listing)
 *   • Category display-label map (backend value → customer-friendly label)
 *
 * Any UI touching seller compliance MUST import from here so the copy
 * stays in sync across the whole marketplace.  Kept in `constants/` per
 * the pre-seller launch sprint spec.
 */

// -------------------------------------------------------------------
// PUBLIC DISCLOSURES — TWO DISTINCT CONSTANTS
// -------------------------------------------------------------------
// Per compliance directive (Feb 2026), the platform maintains TWO
// separate customer-facing disclosures.  They serve different purposes
// and must NOT be substituted for each other.
//
//   1. HOMEMADE_DISCLOSURE — the legally-required home-kitchen
//      statement.  Shown for every seller/product that is presently
//      governed by Alaska's homemade-food workflow.  Do NOT soften,
//      paraphrase, shorten, or replace with generic marketplace copy.
//
//   2. MARKETPLACE_SELLER_DISCLOSURE — general marketplace information
//      that acknowledges Arctic Plate lists different types of
//      independent local food sellers.  May be shown IN ADDITION to
//      HOMEMADE_DISCLOSURE but never as a replacement.
//
// The application currently lacks a verified per-seller classification
// field (home-based vs. licensed commercial, etc.).  Until that field
// exists, we render HOMEMADE_DISCLOSURE for all currently onboarded
// sellers because they are onboarded under the existing home-kitchen
// compliance workflow.  Adding a reliable classifier is tracked as a
// separate task; do not infer classification from ad-hoc heuristics.
// -------------------------------------------------------------------

// Required Alaska home-kitchen statement.  Verbatim per compliance
// directive — do NOT edit the wording without a compliance sign-off.
export const HOMEMADE_DISCLOSURE =
  "This food was made in a home kitchen, is not regulated or " +
  "inspected, except for meat and meat products, and may contain " +
  "allergens.";

// General marketplace preparation-environment notice.  May be shown as
// additional context (e.g., alongside HOMEMADE_DISCLOSURE) or on
// pages/screens where per-seller classification is not yet in scope.
export const MARKETPLACE_SELLER_DISCLOSURE =
  "Products on Arctic Plate may come from different types of " +
  "independent local food kitchens. Preparation environments, permits, " +
  "licensing, inspection status, and required disclosures may differ " +
  "by kitchen and product. Review the kitchen profile, product " +
  "information, and applicable disclosures before ordering.";

// Prohibited-item warning shown to sellers during application, menu
// creation, and category selection.  Rewritten per the pre-seller launch
// follow-up: meat/poultry from USDA-inspected or approved sources ARE
// allowed — only the specific categories listed are blocked for standard
// home-kitchen sellers.
export const PROHIBITED_SELLER_WARNING =
  "Some foods require extra rules. Standard homemade listings cannot " +
  "include seafood, shellfish, game meat, reindeer, raw or unpasteurized " +
  "milk products, nonamenable species, animal-fat oils, controlled " +
  "substances, or foods made with uninspected meat or unapproved " +
  "poultry. Meat or poultry items must use eligible USDA-inspected or " +
  "approved sources.";

// Message shown when a seller attempts to create/publish a restricted item.
export const RESTRICTED_ITEM_BLOCK_MESSAGE =
  "This item may require licensing or may not be allowed under Alaska " +
  "homemade food rules. Contact Arctic Plate before listing.";

// Required seller-responsibility checkboxes.  All three must be checked
// before the seller application can be submitted.
export const SELLER_RESPONSIBILITY_CHECKBOXES = [
  {
    key: "local_rules",
    label:
      "I understand that I am responsible for following Alaska homemade " +
      "food requirements, local city/borough rules, labeling requirements, " +
      "allergen disclosure, and business licensing requirements that apply " +
      "to what I sell.",
  },
  {
    key: "marketplace_only",
    label:
      "I understand that Arctic Plate is a marketplace and does not " +
      "guarantee that my food is legal to sell. I am responsible for only " +
      "listing items I am allowed to sell.",
  },
  {
    key: "prohibited_items",
    label:
      "I understand that home-kitchen businesses may not list seafood, " +
      "shellfish, game meat, reindeer, raw or unpasteurized milk " +
      "products, nonamenable species, animal-fat oils, controlled " +
      "substances, or foods made with uninspected meat or unapproved " +
      "poultry. Meat or poultry items must use eligible USDA-inspected " +
      "or approved sources.",
  },
];

// Prohibited keywords.  Any menu item whose name/tags/category contains
// one of these (case-insensitive substring) is treated as restricted and
// forced to `available=false` (hidden) on save.  Public browse/storefront/
// checkout also filter these out as a defensive backstop.
//
// NOTE: plain "meat", "chicken", "beef", "poultry", and "fish" are
// intentionally NOT here — USDA-inspected/approved-source meat/poultry
// is allowed for standard home-kitchen sellers.  Only the specific
// terms below identify categorically restricted items.
// Seafood-only restrictions.  Under Feb 2026 owner directive, these
// terms only restrict items for HOMEMADE Food Business sellers.
// Permitted Food Business sellers may list fish/seafood when their own
// permits/inspection/sourcing allow it — Arctic Plate does not certify.
export const PROHIBITED_SEAFOOD_KEYWORDS = [
  "seafood",
  "salmon",
  "shellfish",
  "crab",
  "shrimp",
  "halibut",
];

// Non-seafood restrictions apply to BOTH pathways (homemade AND
// permitted).  These are unaffected by the seafood relaxation.
export const PROHIBITED_NON_SEAFOOD_KEYWORDS = [
  "game meat",
  "reindeer",
  "raw milk",
  "unpasteurized milk",
  "uninspected meat",
  "uninspected poultry",
  "unapproved poultry",
  "wild bird egg",
  "nonamenable",
  "non-amenable",
  "animal-fat oil",
  "animal fat oil",
  "tallow oil",
];

// Backward-compat combined export used by callers that still import
// PROHIBITED_KEYWORDS.  Plain "fish" is intentionally NOT in this list.
export const PROHIBITED_KEYWORDS = [
  ...PROHIBITED_SEAFOOD_KEYWORDS,
  ...PROHIBITED_NON_SEAFOOD_KEYWORDS,
];

// Minimum publishable price.  Items priced below this are hidden from the
// public feed (kept in the vendor's private menu list with a warning).
export const MIN_PUBLIC_PRICE_USD = 0.5;

// Customer-facing category label map.  Backend still stores the legacy
// `MenuCategory` Literal values (Meals, Snacks / Small Items, Frozen /
// Meal Prep) alongside the new ones (Hot Meals, Meal Prep, Pantry).
// This map normalises legacy → new labels so the public UI shows a
// consistent, spec-approved category set.
const CATEGORY_DISPLAY_MAP = {
  Meals: "Hot Meals",
  "Frozen / Meal Prep": "Meal Prep",
  "Snacks / Small Items": "Pantry",
};

// The eight spec-approved categories vendors can pick from in the menu
// editor.  Kept in sync with the backend `MenuCategory` Literal.
export const MENU_CATEGORIES = [
  "Hot Meals",
  "Meal Prep",
  "Baked Goods",
  "Desserts",
  "Drinks",
  "Pantry",
  "Sides",
  "Sauces",
];

export function displayCategory(raw) {
  if (!raw) return "Uncategorized";
  return CATEGORY_DISPLAY_MAP[raw] || raw;
}

// Given a raw pickup address like "5831 e 6th ave Anchorage Alaska",
// return public-safe text.  Never returns street numbers.
export function publicPickupArea(rawAddress) {
  if (!rawAddress || typeof rawAddress !== "string") return "Pickup in Alaska";
  const cities = ["Anchorage", "Wasilla", "Palmer", "Fairbanks"];
  const lower = rawAddress.toLowerCase();
  for (const c of cities) {
    if (lower.includes(c.toLowerCase())) return `${c}, AK`;
  }
  return "Alaska";
}

// True if the given menu-item shape (name/tags/category) contains any
// prohibited keyword under the OWNING seller's pathway.
//
//   * ``permitted_food_business`` — only non-seafood keywords apply.
//   * ``homemade_food`` / missing / unknown — homemade fallback: every
//     keyword in ``PROHIBITED_KEYWORDS`` applies.  This preserves the
//     existing homemade behavior verbatim.  Plain ``"fish"`` remains
//     unrestricted.
export function isProhibitedItem(
  { name = "", tags = [], category = "" } = {},
  sellerPathway,
) {
  const haystack = [name, category, ...(Array.isArray(tags) ? tags : [])]
    .join(" ")
    .toLowerCase();
  const keywords =
    sellerPathway === "permitted_food_business"
      ? PROHIBITED_NON_SEAFOOD_KEYWORDS
      : PROHIBITED_KEYWORDS;
  return keywords.some((kw) => haystack.includes(kw));
}


// -------------------------------------------------------------------
// SELLER OPERATING PATHWAY (Feb 2026)
// -------------------------------------------------------------------
// The seller application first asks how the business currently operates
// (homemade vs permitted) — SEPARATE from where the food is prepared.
// A homemade seller may legitimately use a commercial or shared kitchen.
// Backend values are stable and MUST NOT be renamed.
export const SELLER_PATHWAY_OPTIONS = [
  {
    value: "homemade_food",
    title: "Homemade Food Business",
    description:
      "I sell eligible food under Alaska's homemade-food requirements.",
  },
  {
    value: "permitted_food_business",
    title: "Permitted Food Business",
    description:
      "I operate under an applicable food-establishment permit.",
  },
];

export const PREPARATION_SETTING_OPTIONS = [
  { value: "home_kitchen", label: "Home kitchen" },
  { value: "commercial_kitchen", label: "Commercial kitchen" },
  { value: "shared_or_commissary_kitchen", label: "Shared or commissary kitchen" },
  { value: "restaurant_or_food_establishment", label: "Restaurant or food establishment" },
  { value: "food_truck_or_mobile_unit", label: "Food truck or mobile unit" },
  { value: "other", label: "Other" },
];

export const PERMITTED_BUSINESS_TYPE_OPTIONS = [
  { value: "restaurant_or_takeout", label: "Restaurant, deli, or takeout" },
  { value: "caterer", label: "Caterer" },
  { value: "mobile_food_unit", label: "Food truck, cart, or mobile unit" },
  { value: "meal_prep_or_prepared_food", label: "Meal-prep or prepared-food business" },
  { value: "bakery", label: "Bakery" },
  { value: "coffee_shop_or_espresso_stand", label: "Coffee shop or espresso stand" },
  { value: "retail_food_market", label: "Retail food market" },
  { value: "food_processor_or_packaged_food", label: "Food processor or packaged-food producer" },
  { value: "other", label: "Other permitted food business" },
];

// Notice shown to a permitted-food-business applicant instead of (never
// in addition to) the homemade disclosure.  Do NOT paraphrase the
// homemade statement here — the two disclosures are distinct.
export const PERMITTED_BUSINESS_RESPONSIBILITY_NOTICE =
  "You are applying through Arctic Plate's permitted food business " +
  "pathway. You remain responsible for maintaining every permit, " +
  "business license, inspection requirement, food-safety requirement, " +
  "label, and approval that applies to your operation and products. " +
  "Arctic Plate's review is a marketplace-readiness review and is not a " +
  "government inspection or legal certification.";

export const PERMITTED_BUSINESS_RESTRICTED_CATEGORY_WARNING =
  "Some regulated food categories remain hidden on Arctic Plate until a " +
  "separate document and category review is available. Selecting the " +
  "permitted food business pathway does not automatically unlock " +
  "seafood, shellfish, game meat, reindeer, raw or unpasteurized milk " +
  "products, or other currently restricted categories.";

// Shared acknowledgements — checked by both pathways.
export const COMMON_ACKNOWLEDGEMENTS = [
  {
    key: "local_rules",
    label:
      "I am responsible for applicable Alaska and local requirements for " +
      "what I sell.",
  },
  {
    key: "marketplace_only",
    label:
      "I understand Arctic Plate is a marketplace and does not guarantee " +
      "that an item is legal to sell.",
  },
];

// Homemade-only acknowledgements — never shown for permitted pathway.
export const HOMEMADE_ACKNOWLEDGEMENTS = [
  {
    key: "homemade_food_disclosure",
    label:
      "I accept the exact homemade-food disclosure shown above.",
  },
  {
    key: "prohibited_items",
    label:
      "I understand seafood, shellfish, game meat, reindeer, raw or " +
      "unpasteurized milk products, and other currently restricted " +
      "homemade-food categories may not be listed on Arctic Plate.",
  },
];

// Permitted-only acknowledgements — never shown for homemade pathway.
export const PERMITTED_ACKNOWLEDGEMENTS = [
  {
    key: "permit_responsibility",
    label:
      "I remain responsible for permits, licensing, inspection " +
      "requirements, food safety, product eligibility, labeling, " +
      "renewals, and every other requirement that applies to my operation.",
  },
];

// Pure helper — returns the customer-facing disclosure text for the
// given vendor shape.  Legacy vendors (missing seller_pathway) fall
// back to HOMEMADE_DISCLOSURE because the entire onboarded fleet was
// captured under the homemade workflow before this field existed.
// NEVER write this fallback anywhere; storage stays truthful.
export function sellerDisclosureForVendor(vendor) {
  if (vendor && vendor.seller_pathway === "permitted_food_business") {
    return PERMITTED_BUSINESS_RESPONSIBILITY_NOTICE;
  }
  return HOMEMADE_DISCLOSURE;
}

